import { makeAutoObservable, runInAction } from "mobx";
import { CraCommentApi } from "../services/CraCommentApi";
import {
  CraComment,
  CreateCraComment,
  UpdateCraComment,
} from "../models/CraComment";
import { RootStore } from "../../shared/stores/RootStore";
import { CraWayApi } from "../../cra/services/CraWayApi";

export class CraCommentStore {
  rootStore;
  commentsByCraWayId: Map<string, CraComment[]> = new Map();
  showReviewNodeAttributes = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetState() {
    runInAction(() => {
      this.commentsByCraWayId = new Map();
      this.showReviewNodeAttributes = false;
    });
  }

  setShowReviewNodeAttributes(value: boolean) {
    runInAction(() => {
      this.showReviewNodeAttributes = value;
    });
  }

  async fetchComments(craWayId: string, cancelIf?: () => boolean) {
    const { data: comments } = await CraWayApi.listComments(
      craWayId
    );
    if (cancelIf?.()) return;
    runInAction(() => {
      this.commentsByCraWayId.set(craWayId, comments);
      this.commentsByCraWayId = new Map(
        this.commentsByCraWayId
      );
    });
  }

  async postComment(request: CreateCraComment) {
    const { data: comment } = await CraCommentApi.create(request);

    runInAction(() => {
      // if comment is a reply, add it at the end of its main thread's replies
      this.updateCraWayComments(request.craWayId, (prev) => {
        if (comment.mainThreadId) {
          return prev.map((mainThread) => {
            if (mainThread.id === comment.mainThreadId) {
              return {
                ...mainThread,
                replies: [...mainThread.replies, comment],
              };
            }
            return mainThread;
          });
        } else {
          return [...prev, comment];
        }
      });
    });
  }

  private updateCraWayComments(
    craWayId: string,
    callback: (prev: CraComment[]) => CraComment[]
  ) {
    const comments =
      this.commentsByCraWayId.get(craWayId) ?? [];
    if (comments) {
      this.commentsByCraWayId.set(craWayId, callback(comments));
      this.commentsByCraWayId = new Map(
        this.commentsByCraWayId
      );
    }
  }

  async updateComment(commentId: string, request: UpdateCraComment) {
    const { data: comment } = await CraCommentApi.update(commentId, request);
    runInAction(() => {
      this.updateCraWayComments(comment.craWayId, (prev) => {
        return prev.map((mainThread) => {
          // if comment is a reply, go through the replies of its main thread
          if (comment.mainThreadId) {
            if (mainThread.id !== comment.mainThreadId) return mainThread;
            return {
              ...mainThread,
              replies: mainThread.replies.map((reply) =>
                reply.id === commentId ? comment : reply
              ),
            };
          }
          // else, replace the corresponding main thread
          return mainThread.id === commentId ? comment : mainThread;
        });
      });
    });
  }

  async resolveComment(commentId: string) {
    const response = await CraCommentApi.resolve(commentId);
    runInAction(() => {
      this.updateCraWayComments(
        response.data.craWayId,
        (prev) => {
          return prev.map((comment) =>
            comment.id === commentId ? response.data : comment
          );
        }
      );
    });
  }

  async unresolveComment(commentId: string) {
    const response = await CraCommentApi.unresolve(commentId);
    runInAction(() => {
      this.updateCraWayComments(
        response.data.craWayId,
        (prev) => {
          return prev.map((comment) =>
            comment.id === commentId ? response.data : comment
          );
        }
      );
    });
  }

  async deleteComment(comment: CraComment) {
    await CraCommentApi.delete(comment.id);
    runInAction(() => {
      this.updateCraWayComments(comment.craWayId, (prev) => {
        return (
          prev
            // if comment is a main thread, delete it like this
            .filter((mainThread) => mainThread.id !== comment.id)
            // if comment is a reply, delete it like this
            .map((mainThread) => ({
              ...mainThread,
              replies: mainThread.replies.filter(
                (reply) => reply.id !== comment.id
              ),
            }))
        );
      });
    });
  }
}
